import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Statistic, DatePicker, Spin, message, Table, Progress } from 'antd';
import { ClockCircleOutlined, CheckCircleOutlined, HourglassOutlined, TeamOutlined, BookOutlined, UserOutlined } from '@ant-design/icons';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { dashboardService } from '../services/api';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

const DashboardOperacional = () => {
  const [loading, setLoading] = useState(true);
  const [dados, setDados] = useState(null);
  const [mesAno, setMesAno] = useState(dayjs());

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D'];

  useEffect(() => {
    carregarDados();
  }, [mesAno]);

  const carregarDados = async () => {
    setLoading(true);
    try {
      const params = {
        mes: mesAno.month() + 1,
        ano: mesAno.year()
      };

      const response = await dashboardService.getOperacional(params);
      setDados(response.data);
    } catch (error) {
      message.error('Erro ao carregar dados do dashboard operacional');
      console.error(error);
    }
    setLoading(false);
  };

  if (loading || !dados) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <Spin size="large" tip="Carregando dashboard..." />
      </div>
    );
  }

  const { kpis, aulas_por_modalidade, top_professores, turmas_ocupacao } = dados;

  const colunasTopProfessores = [
    {
      title: 'Professor',
      dataIndex: 'professor',
      key: 'professor',
    },
    {
      title: 'Aulas',
      dataIndex: 'aulas',
      key: 'aulas',
      align: 'center',
    },
    {
      title: 'Horas',
      dataIndex: 'horas',
      key: 'horas',
      align: 'center',
      render: (horas) => `${horas}h`,
    },
  ];

  const colunasTurmasOcupacao = [
    {
      title: 'Turma',
      dataIndex: 'turma',
      key: 'turma',
      width: 150,
    },
    {
      title: 'Escola',
      dataIndex: 'escola',
      key: 'escola',
      width: 200,
    },
    {
      title: 'Modalidade',
      dataIndex: 'modalidade',
      key: 'modalidade',
      width: 150,
    },
    {
      title: 'Alunos',
      dataIndex: 'alunos',
      key: 'alunos',
      align: 'center',
      width: 80,
    },
    {
      title: 'Capacidade',
      dataIndex: 'capacidade',
      key: 'capacidade',
      align: 'center',
      width: 100,
    },
    {
      title: 'Ocupação',
      key: 'ocupacao',
      width: 150,
      render: (_, record) => (
        <Progress
          percent={record.ocupacao}
          size="small"
          status={record.ocupacao >= 90 ? 'exception' : record.ocupacao >= 70 ? 'normal' : 'success'}
        />
      ),
    },
  ];

  return (
    <div className="dashboard-page" style={{ padding: '24px' }}>
      {/* Cabeçalho */}
      <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
        <Col>
          <h1 style={{ margin: 0 }}>Dashboard Operacional</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Acompanhamento de aulas e turmas</p>
        </Col>
        <Col>
          <DatePicker
            picker="month"
            value={mesAno}
            onChange={(date) => setMesAno(date || dayjs())}
            format="MMMM/YYYY"
            style={{ width: 200 }}
          />
        </Col>
      </Row>

      {/* KPIs Principais */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Aulas no Mês"
              value={kpis.aulas_mes.total}
              prefix={<BookOutlined />}
              valueStyle={{ color: '#1890ff' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              ✅ {kpis.aulas_mes.validadas} validadas | ⏳ {kpis.aulas_mes.pendentes} pendentes
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Taxa de Validação"
              value={kpis.aulas_mes.taxa_validacao}
              precision={1}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: kpis.aulas_mes.taxa_validacao >= 80 ? '#52c41a' : '#faad14' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Meta: 90%
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Horas Ministradas"
              value={kpis.horas_ministradas}
              precision={1}
              suffix="h"
              prefix={<ClockCircleOutlined />}
              valueStyle={{ color: '#722ed1' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              Aulas validadas
            </p>
          </Card>
        </Col>

        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Média Alunos/Turma"
              value={kpis.media_alunos_turma}
              precision={1}
              prefix={<UserOutlined />}
              valueStyle={{ color: '#eb2f96' }}
            />
            <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
              {kpis.professores_ativos} professores ativos
            </p>
          </Card>
        </Col>
      </Row>

      {/* Gráficos */}
      <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
        <Col xs={24} lg={12}>
          <Card title="Aulas por Modalidade">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={aulas_por_modalidade}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="modalidade" />
                <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                <Tooltip />
                <Legend />
                <Bar yAxisId="left" dataKey="aulas" fill="#8884d8" name="Aulas" />
                <Bar yAxisId="right" dataKey="horas" fill="#82ca9d" name="Horas" />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card title="Distribuição de Horas por Modalidade">
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={aulas_por_modalidade}
                  dataKey="horas"
                  nameKey="modalidade"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={(entry) => `${entry.modalidade}: ${entry.horas}h`}
                >
                  {aulas_por_modalidade.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>

      {/* Tabelas */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="Top 5 Professores" extra={<TeamOutlined />}>
            <Table
              dataSource={top_professores}
              columns={colunasTopProfessores}
              pagination={false}
              size="small"
              rowKey={(record, index) => index}
            />
          </Card>
        </Col>

        <Col xs={24}>
          <Card title="Ocupação das Turmas" extra="Top 10">
            <Table
              dataSource={turmas_ocupacao}
              columns={colunasTurmasOcupacao}
              pagination={{ pageSize: 5 }}
              size="small"
              rowKey={(record, index) => index}
              scroll={{ x: 800 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardOperacional;
